import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api, getErrorMessage } from '@/utils';
import { useSnackbar } from '@/context/SnackbarContext';
import { API_ENDPOINTS } from '@/constants';

import type { CampaignQuestion } from '../types';

interface SaveQuestionsResponse {
    success: boolean;
    questions: CampaignQuestion[];
}

interface SaveQuestionsParams {
    campaignId: number;
    questions: CampaignQuestion[];
}

/**
 * Saves campaign questions.
 **/
const useSaveQuestions = () => {
    const queryClient = useQueryClient();
    const { showSnackbar } = useSnackbar();

    return useMutation({
        mutationFn: async ({ campaignId, questions }: SaveQuestionsParams) => {
            const response = await api.post(API_ENDPOINTS.CAMPAIGN_QUESTIONS(campaignId), { questions });

            return response.data as SaveQuestionsResponse;
        },
        onSuccess: (data, { campaignId }) => {
            queryClient.invalidateQueries({ queryKey: ['api', 'campaign', campaignId] });
            queryClient.invalidateQueries({ queryKey: ['qualityScoreFormula', campaignId] });
            queryClient.invalidateQueries({ queryKey: ['api', 'call'] });
            showSnackbar('Questions saved successfully', 'success');

            return data.questions;
        },
        onError: error => {
            // eslint-disable-next-line no-console
            console.error('Error saving questions:', error);
            showSnackbar(getErrorMessage(error) ?? 'Failed to save questions', 'error');
        },
    });
};

export default useSaveQuestions;
